import { useMemo, useState } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Animal, Breed, BreedType, Gender, PetAge } from 'shared/@generated/models';
import markets from 'shared/@generated/markets.json';
import { BreedPickerPanel } from 'shared/components/breed-picker-panel';
import { FilterOverviewRow, SelectFilterPanel, SliderFilterPanel } from './web-filter-controls';
import { LocationFilterPanel } from 'shared/components/location-filter-panel.web';
import { useFormatter } from 'shared/library/use-formatter';
import { useBreeds } from 'shared/state/breeds';
import type { SearchModalFiltersProps } from './search-modal-filters.types';
import {
  getSavedSearchBreedsForAnimal,
  hasSavedSearchLocation,
} from 'shared/library/saved-search-data';
import type { SearchFilterPanel } from './search-modal-filters.types';

type SelectOption<T> = {
  label: string;
  value: T | null;
};

const NOOP_PANEL_CHANGE = (_panel: SearchFilterPanel) => {};

export function SearchModalFilters({
  data,
  panel = 'overview',
  onPanelChange = NOOP_PANEL_CHANGE,
  onChange,
}: SearchModalFiltersProps) {
  const { t } = useTranslation('components');
  const { formatCurrency } = useFormatter();
  const currencySuffix = formatCurrency(0).replace(/[\d\s.,]/g, '');
  const priceMarks = [1000, 10000, 20000, 30000, 40000, 50000].map((value) => ({
    value,
    label: `${formatCurrency(value).replace(currencySuffix, '').trim()}${value === 50000 ? '+' : ''}`,
  }));
  const { breeds, loading } = useBreeds();
  const [breedQuery, setBreedQuery] = useState('');
  const coordinate = hasSavedSearchLocation(data)
    ? { latitude: data.latitude, longitude: data.longitude }
    : null;
  const hasLocation = coordinate !== null && data.maxDistanceInKm !== null;

  const availableBreeds = useMemo(() => {
    const query = breedQuery.trim().toLocaleLowerCase();

    return breeds
      .filter((breed) => !data.animal || breed.animal === data.animal)
      .filter((breed) => !query || breed.name.toLocaleLowerCase().includes(query))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [breedQuery, breeds, data.animal]);

  const selectedBreeds = (data.breeds ?? []).map((value) => ({
    value,
    label:
      typeof value === 'number'
        ? (breeds.find((breed) => breed.id === value)?.name ??
          t('listings_filter_form.selected_breed'))
        : value,
  }));

  const countryOptions: SelectOption<string>[] = [
    { label: t('listings_filter_form.all_countries'), value: null },
    ...markets.map((market) => ({
      label: t(`common:enums.country_code.${market.country_code}`),
      value: market.country_code,
    })),
  ];
  const animalOptions: SelectOption<Animal>[] = [
    { label: t('listings_filter_form.all_animals'), value: null },
    ...Object.values(Animal).map((value) => ({
      label: t(`common:enums.animal.${value}`),
      value,
    })),
  ];
  const breedTypeOptions: SelectOption<BreedType>[] = [
    { label: t('listings_filter_form.all_breed_types'), value: null },
    ...Object.values(BreedType)
      .filter((value) => value !== BreedType.Crossbreed)
      .map((value) => ({
        label:
          value === BreedType.Purebred
            ? t('search_modal.purebred')
            : t(`common:enums.breed_type.${value}`),
        value,
      })),
  ];
  const genderOptions: SelectOption<Gender>[] = [
    { label: t('listings_filter_form.all_genders'), value: null },
    ...Object.values(Gender).map((value) => ({
      label: t(`common:enums.gender.${value}`),
      value,
    })),
  ];
  const petAgeOptions: SelectOption<PetAge>[] = [
    { label: t('listings_filter_form.all_ages'), value: null },
    ...Object.values(PetAge).map((value) => ({
      label: t(`common:enums.pet_age.${value}`),
      value,
    })),
  ];
  const sellerOptions: SelectOption<boolean>[] = [
    { label: t('listings_filter_form.all_sellers'), value: null },
    { label: t('listings_filter_form.only_breeders'), value: true },
  ];

  const labels = {
    breeds:
      selectedBreeds.length > 0
        ? selectedBreeds.map((breed) => breed.label).join(', ')
        : t('listings_filter_form.all_breeds'),
    country: selectedOptionLabel(countryOptions, data.country),
    animal: selectedOptionLabel(animalOptions, data.animal),
    breedType: selectedOptionLabel(breedTypeOptions, data.breedType),
    gender: selectedOptionLabel(genderOptions, data.gender),
    seller: selectedOptionLabel(sellerOptions, data.onlyBreeders),
    petAge: selectedOptionLabel(petAgeOptions, data.petAge),
    maxPrice:
      data.maxPrice === null
        ? t('listings_filter_form.no_max_price')
        : formatCurrency(data.maxPrice),
    distance: hasLocation ? `${data.maxDistanceInKm} km` : t('listings_filter_form.none'),
  };

  function setBreeds(values: (string | number)[]) {
    onChange({ breeds: values.length > 0 ? values : [] });
  }

  function toggleBreed(breed: Breed) {
    const selected = data.breeds ?? [];
    setBreeds(
      selected.includes(breed.id)
        ? selected.filter((value) => value !== breed.id)
        : [...selected, breed.id],
    );
  }

  if (panel === 'overview') {
    return (
      <View className={'mt-1.5'} testID={'search-filter-overview'}>
        <FilterOverviewRow
          label={t('listings_filter_form.breed')}
          value={labels.breeds}
          active={selectedBreeds.length > 0}
          testID={'search-filter-breed'}
          onPress={() => onPanelChange('breeds')}
        />
        <FilterOverviewRow
          label={t('listings_filter_form.country')}
          value={labels.country}
          active={Boolean(data.country)}
          onPress={() => onPanelChange('country')}
        />
        <FilterOverviewRow
          label={t('listings_filter_form.animal')}
          value={labels.animal}
          active={Boolean(data.animal)}
          onPress={() => onPanelChange('animal')}
        />
        <FilterOverviewRow
          label={t('listings_filter_form.breed_type')}
          value={labels.breedType}
          active={Boolean(data.breedType)}
          onPress={() => onPanelChange('breedType')}
        />
        <FilterOverviewRow
          label={t('listings_filter_form.gender')}
          value={labels.gender}
          active={Boolean(data.gender)}
          onPress={() => onPanelChange('gender')}
        />
        <FilterOverviewRow
          label={t('listings_filter_form.seller')}
          value={labels.seller}
          active={Boolean(data.onlyBreeders)}
          onPress={() => onPanelChange('seller')}
        />
        <FilterOverviewRow
          label={t('listings_filter_form.age')}
          value={labels.petAge}
          active={Boolean(data.petAge)}
          onPress={() => onPanelChange('petAge')}
        />
        <FilterOverviewRow
          label={t('listings_filter_form.max_price')}
          value={labels.maxPrice}
          active={data.maxPrice !== null}
          onPress={() => onPanelChange('maxPrice')}
        />
        <FilterOverviewRow
          label={t('listings_filter_form.location')}
          value={labels.distance}
          active={hasLocation && data.maxDistanceInKm !== null}
          testID={'search-filter-location'}
          onPress={() => onPanelChange('distance')}
        />
      </View>
    );
  }

  return (
    <View className={'min-h-0 flex-1'}>
      {panel === 'breeds' && (
        <BreedPickerPanel
          query={breedQuery}
          setQuery={setBreedQuery}
          breeds={availableBreeds}
          allBreeds={breeds}
          loading={loading}
          selected={data.breeds ?? []}
          getQueryLabel={(query) => t('search_modal.search_for_query', { query })}
          onToggle={toggleBreed}
          onRemove={(value) => setBreeds((data.breeds ?? []).filter((breed) => breed !== value))}
          onSelectQuery={(value) => {
            setBreeds(Array.from(new Set([...(data.breeds ?? []), value])));
          }}
        />
      )}
      {panel === 'country' && (
        <SelectFilterPanel
          options={countryOptions}
          value={data.country}
          onChange={(country) => onChange({ country })}
        />
      )}
      {panel === 'animal' && (
        <SelectFilterPanel
          options={animalOptions}
          value={data.animal}
          onChange={(animal) =>
            onChange({
              animal,
              breeds: getSavedSearchBreedsForAnimal(data.breeds, animal, breeds),
            })
          }
        />
      )}
      {panel === 'breedType' && (
        <SelectFilterPanel
          options={breedTypeOptions}
          value={data.breedType}
          onChange={(breedType) => onChange({ breedType })}
        />
      )}
      {panel === 'gender' && (
        <SelectFilterPanel
          options={genderOptions}
          value={data.gender}
          onChange={(gender) => onChange({ gender })}
        />
      )}
      {panel === 'seller' && (
        <SelectFilterPanel
          options={sellerOptions}
          value={data.onlyBreeders}
          onChange={(onlyBreeders) => onChange({ onlyBreeders })}
        />
      )}
      {panel === 'petAge' && (
        <SelectFilterPanel
          options={petAgeOptions}
          value={data.petAge}
          onChange={(petAge) => onChange({ petAge })}
        />
      )}
      {panel === 'maxPrice' && (
        <View className={'px-5 py-8 md:px-8'}>
          <SliderFilterPanel
            value={data.maxPrice}
            valueLabel={labels.maxPrice}
            marks={priceMarks}
            suffix={currencySuffix}
            min={1000}
            max={50000}
            roundTo={1000}
            accessibilityLabel={t('listings_filter_form.max_price')}
            onChange={(maxPrice) => onChange({ maxPrice: maxPrice === 50000 ? null : maxPrice })}
          />
        </View>
      )}
      {panel === 'distance' && (
        <LocationFilterPanel
          coordinate={coordinate}
          maxDistanceInKm={data.maxDistanceInKm}
          country={data.country}
          onChange={onChange}
          onClear={() => onChange({ latitude: null, longitude: null, maxDistanceInKm: null })}
        />
      )}
    </View>
  );
}

function selectedOptionLabel<T>(options: SelectOption<T>[], value: T | null) {
  return options.find((option) => option.value === value)?.label ?? options[0]!.label;
}
